import React, { useState } from 'react';
import { Eye, Loader2, Save } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../../ui/dialog';
import { TAB_COLORS, TABS, PLACEHOLDER_HINTS, TemplateText, isMlsCategory } from './chipsConfig';

const TITLES = {
  create: ['Add Chip', 'Create a new quick-select reason for this category.'],
  edit: ['Edit Chip', 'Changes apply to new selections; historical order notes are never rewritten.'],
  duplicate: ['Duplicate Chip', 'A new chip will be created from this copy — the original stays untouched.'],
};

const EditorForm = ({ mode, chip, category, onSave, onClose }) => {
  const isMls = isMlsCategory(category);
  const [label, setLabel] = useState(mode === 'duplicate' ? `${chip?.label || ''} (Copy)` : (chip?.label || ''));
  const [template, setTemplate] = useState(chip?.template || '');
  const [automation, setAutomation] = useState(chip?.automation || '');
  const [saving, setSaving] = useState(false);
  const colors = TAB_COLORS[category];
  const tab = TABS.find(t => t.key === category);
  const Icon = tab.icon;
  const templateRequired = mode !== 'edit';
  const canSave = label.trim().length > 0 && (!templateRequired || template.trim().length > 0) && !saving;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSave) return;
    setSaving(true);
    const ok = await onSave({ label: label.trim(), template: template.trim(), automation: category === 'pending' ? (automation || null) : null });
    setSaving(false);
    if (ok) onClose();
  };

  return (
    <form onSubmit={submit} style={{ '--qc-accent': colors.accent, '--qc-soft': colors.soft, '--qc-tile': colors.tile, '--qc-text': colors.text }}>
      <div className="qc-dialog-head">
        <span className="qc-dialog-cat" data-testid="chip-editor-category"><Icon className="w-3.5 h-3.5" />{tab.label}</span>
        <DialogTitle className="text-xl font-extrabold tracking-tight text-slate-900 mt-3" data-testid="chip-editor-title">{TITLES[mode][0]}</DialogTitle>
        <DialogDescription className="text-sm text-slate-500 mt-1">{TITLES[mode][1]}</DialogDescription>
      </div>

      <div className="qc-dialog-body">
        <div className="qc-field">
          <label htmlFor="qc-label">Chip Label</label>
          <input id="qc-label" value={label} onChange={e => setLabel(e.target.value)} placeholder="e.g., Awaiting callback from jurisdiction" autoFocus data-testid="chip-editor-label" />
        </div>
        <div className="qc-field">
          <label htmlFor="qc-template">Template{templateRequired ? '' : ' (optional)'}</label>
          <textarea id="qc-template" value={template} onChange={e => setTemplate(e.target.value)} placeholder={isMls ? 'Plain-text note inserted for the processor…' : 'Template text with {placeholders}…'} data-testid="chip-editor-template" />
          {isMls ? (
            <p className="qc-field-hint">MLS templates are plain text — placeholders are not resolved for MLS.</p>
          ) : (
            <div className="qc-insert" data-testid="chip-editor-placeholders">
              {PLACEHOLDER_HINTS.map(p => (
                <button key={p.tag} type="button" title={p.desc} onClick={() => setTemplate(prev => prev + p.tag)}>{p.tag}</button>
              ))}
            </div>
          )}
        </div>
        {category === 'pending' && (
          <div className="qc-field">
            <label htmlFor="qc-automation">Automation</label>
            <select id="qc-automation" value={automation} onChange={e => setAutomation(e.target.value)} data-testid="chip-editor-automation">
              <option value="">None</option>
              <option value="tax_collector_email">Send Tax Collector email (Tax only)</option>
            </select>
          </div>
        )}

        <div className="qc-preview" data-testid="chip-editor-preview">
          <div className="qc-preview-title"><Eye className="w-3.5 h-3.5" />Live preview — what the processor sees</div>
          <span className="qc-preview-chip" data-testid="chip-editor-preview-chip">{label.trim() || 'Chip label'}</span>
          <div className="qc-preview-note" data-testid="chip-editor-preview-note">
            <TemplateText template={template} emptyText={templateRequired ? 'Template text will appear here…' : 'No template — processor adds a note'} />
          </div>
        </div>
      </div>

      <div className="qc-dialog-foot">
        <button type="button" className="qc-btn qc-btn-ghost" onClick={onClose} data-testid="chip-editor-cancel">Cancel</button>
        <button type="submit" className="qc-btn qc-btn-primary" disabled={!canSave} data-testid="chip-editor-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {mode === 'edit' ? 'Save Changes' : 'Save Chip'}
        </button>
      </div>
    </form>
  );
};

export const ChipEditorDialog = ({ editor, onSave, onClose }) => (
  <Dialog open={!!editor} onOpenChange={(open) => { if (!open) onClose(); }}>
    <DialogContent className="qc-dialog max-w-2xl" data-testid="chip-editor-dialog">
      {editor && <EditorForm key={`${editor.mode}-${editor.chip?.id || 'new'}`} {...editor} onSave={onSave} onClose={onClose} />}
    </DialogContent>
  </Dialog>
);
